import {useItemList} from "../utils/hooks.ts";
import {config} from "../config.ts";
import {DateTime} from "luxon";
import ld from '../svg/left-decoration.svg'
import {ItemListItemClass} from "../types/ItemListItemClass.ts";
import ReactDOM from "react-dom/client";
import React, {useEffect} from "react";
import GetLabel from "../language/GetLabel.tsx";
import LiveCountdown from "../date/LiveCountdown.tsx";
import { header_liveBarClickHandler } from "../utils/header-utils.ts";
import {setElementVisible} from "../utils/common-utils.ts";

export const renderLiveBar = (div: string) => {
    const root = document.getElementById(div)
    if (root) {
        ReactDOM.createRoot(root).render(<React.StrictMode><LiveContainer/></React.StrictMode>)
    }
}
const LiveContainer = () => {
    // Load the home livestreams list through the SAME item-list.json endpoint the home-livestreams
    // section uses; identical SWR key (localId  + removeCpacTv + lang + generation) ⇒ on the home page the two
    // collapse onto ONE deduped fetch. The home page's hide_CPACTV_b is enforced server-side by the
    // endpoint (CPACWEB-435), so the bar honours it on every page. refreshInterval keeps the bar
    // current site-wide, so we don't need the DOM watchdog here — generation stays 0 (see hooks.ts / plan-livestreams-shared-fetch).
    const {list} = useItemList("livestreams", "", undefined, 0,
        config.LIVESTREAMS_LOCAL_ID, false, config.liveStreamReloadMillis)

    // list.items is already IGNORE/no-URL filtered by ItemListClass.getItems. Keep the CPACWEB-277
    // corrupt-prelive guard (a prelive item with no liveDateTime throws inside renderSmallItemInfo
    // and would unwind the whole livebar subtree).
    const items: ItemListItemClass[] = (list?.items ?? []).filter(
        (item) => !(item.type === "prelive" && !item.liveDateTime)
    )

    // Header live-stream counter + live-bar button visibility (moved out of the old loadLiveStreams).
    // Runs on every change, including when the list empties, so the bar hides itself.
    useEffect(() => {
        const count = items.length
        setElementVisible("liveBarClickHandler", count > 0)
        const counter = document.getElementById("header_livestream_counter")
        if (counter) {
            counter.innerHTML = "" + count
        } else {
            console.warn("Could not set livestream count because the header_livestream_counter element was not present")
        }
    }, [items.length])

    if (items.length === 0) {
        return null
    }

    const renderLive = () => {
        return (
            <div className="video-live-label t-l">
                <img src="/static-assets/theme/svg/target.svg" alt="target-icon"/>
                <span><GetLabel name="LIVE"/></span>
            </div>
        )
    }

    const renderCpactv = () => {
        return (
            <div className="live-container__item__cpactv">
                <img src={ld} alt="" aria-hidden="true"/>
                <span>CPAC TV</span>
            </div>
        )
    }

    const renderSmallItemInfo = (item: ItemListItemClass) => {
        const minutesBefore = config.liveNotificationTime
        const now = DateTime.now()
        // Guard against a corrupt item with no liveDateTime (the validItems filter only covers prelive items);
        // an uncaught throw here unwinds the whole livebar subtree.
        const alertTime = item.liveDateTime ? item.liveDateTime.minus(minutesBefore) : null
        const eventIsSoon = alertTime != null && now > alertTime && now < item.liveDateTime;
        const alreadyStarted = item.liveDateTime != null && now > item.liveDateTime;

        const renderTest = () => {/* addition for test flag */
			return (
				<div className="video-test-label t-r">
					<span>TEST</span>
				</div>
			)
		}

        switch (item.type) {

            case "live":
                return (
                    <>
                        <div className="live-container__item__header">
                            <a className="live-container__item__image" href={item.url} tabIndex={-1} aria-label={item.category && item.title ? item.category + " - " + item.title : ""}><img loading="lazy" className="multi-thumb" data-src={item.image} alt={item.category && item.altImageTxt ? item.altImageTxt : item.category + " - " + item.title}/></a>
                        </div>
                        <div className="live-container__item__description">
                            <a href={item.categoryURL} className="live-container__item__category" tabIndex={-1}>{item.category}</a>
                            <a href={item.url} className="live-container__item__title" tabIndex={-1} aria-label={item.category && item.title ? item.title + " - " + item.category : ""}>{item.title}</a>
                        </div>
                        {renderLive()}
                        {item.testStream === "true" ? renderTest() : ""}
                    </>
                )
                break;
            case "prelive":
                if (eventIsSoon === false) {
                    if (alreadyStarted) {
                        return (
                            <>
                                <div className="live-container__item__header">
                                    <a className="live-container__item__image" href={item.url} tabIndex={-1} aria-label={item.category && item.title ? item.category + " - " + item.title : ""}><img loading="lazy" className="multi-thumb" data-src={item.image} alt={item.category && item.altImageTxt ? item.altImageTxt : item.category + " - " + item.title}/></a>
                                </div>
                                <div className="live-container__item__description">
                                    <a href={item.categoryURL} className="live-container__item__category" tabIndex={-1}>{item.category}</a>
                                    <a href={item.url} className="live-container__item__title" tabIndex={-1} aria-label={item.category && item.title ? item.title + " - " + item.category : ""}>{item.title}</a>
                                </div>
                                {renderLive()}
                                {item.testStream === "true" ? renderTest() : ""}
                            </>
                        )
                    } else {
                        return (
                            <>
                                <div className="live-container__item__header">
                                    <a className="live-container__item__image" href={item.url} tabIndex={-1} aria-label={item.category && item.title ? item.category + " - " + item.title : ""}><img loading="lazy" className="multi-thumb" data-src={item.image} alt={item.category && item.altImageTxt ? item.altImageTxt : item.category + " - " + item.title}/></a>
                                    <div className="live-container__item__live-info" aria-live="polite">
                                        <LiveCountdown liveDateTime={item.liveDateTime} />
                                    </div>
                                </div>
                                <div className="live-container__item__description">
                                    <a href={item.categoryURL} className="live-container__item__category" tabIndex={-1}>{item.category}</a>
                                    <a href={item.url} className="live-container__item__title" tabIndex={-1} aria-label={item.category && item.title ? item.title + " - " + item.category : ""}>{item.title}</a>
                                </div>
                                {item.testStream === "true" ? renderTest() : ""}
                            </>
                        )
                    }
                } else {
                    return (
                        <>
                            <div className="live-container__item__header">
                                <a className="live-container__item__image" href={item.url} tabIndex={-1} aria-label={item.category && item.title ? item.category + " - " + item.title : ""}><img loading="lazy" className="multi-thumb" data-src={item.image} alt={item.category && item.altImageTxt ? item.altImageTxt : item.category + " - " + item.title}/></a>
                                <div className="live-container__item__live-info" aria-live="polite">
                                    <GetLabel name="LIVE"/>
                                    <LiveCountdown liveDateTime={item.liveDateTime} />
                                </div>
                            </div>
                            <div className="live-container__item__description">
                                <a href={item.categoryURL} className="live-container__item__category" tabIndex={-1}>{item.category}</a>
                                <a href={item.url} className="live-container__item__title" tabIndex={-1} aria-label={item.category && item.title ? item.title + " - " + item.category : ""}>{item.title}</a>
                            </div>
                            {item.testStream === "true" ? renderTest() : ""}
                        </>
                    )
                }
                break;
            case "cpactv":
                return (
                    <>
                        <div className="live-container__item__header">
                            <a className="live-container__item__image" href={item.url} tabIndex={-1} aria-label={item.category && item.title ? item.category + " - " + item.title : ""}><img loading="lazy" className="multi-thumb" data-src={item.image} alt={item.category && item.altImageTxt ? item.altImageTxt : item.category + " - " + item.title}/></a>
                        </div>
                        <div className="live-container__item__description">
                            <a href={item.categoryURL} className="live-container__item__category" tabIndex={-1}>{item.category}</a>
                            <a href={item.url} className="live-container__item__title" tabIndex={-1} aria-label={item.category && item.title ? item.title + " - " + item.category : ""}>{item.title}</a>
                        </div>
                        {renderCpactv()}
                    </>
                )
                break;
            default:
                return (
                    <>
                        <div className="live-container__item__header">
                            <a className="live-container__item__image" href={item.url} tabIndex={-1} aria-label={item.category && item.title ? item.category + " - " + item.title : ""}><img loading="lazy" className="multi-thumb" data-src={item.image} alt={item.category && item.altImageTxt ? item.altImageTxt : item.category + " - " + item.title}/></a>
                        </div>
                        <div className="live-container__item__description">
                            <a href={item.categoryURL} className="live-container__item__category" tabIndex={-1}>{item.category}</a>
                            <a href={item.url} className="live-container__item__title" tabIndex={-1} aria-label={item.category && item.title ? item.title + " - " + item.category : ""}>{item.title}</a>
                        </div>
                        {item.testStream === "true" ? renderTest() : ""}
                    </>
                )
        }
    }

    const renderItems = () => {
        return items.map((item, i) => {
            return (
                <li key={i} className={`live-container__item`} tabIndex={-1}>
                    {renderSmallItemInfo(item)}
                </li>
            );
        });
    }
    const renderCloseBtn = () => {
        return <button onClick={header_liveBarClickHandler} className="closeLive" tabIndex={-1} aria-controls="live-bar"><GetLabel name="CLOSE"/></button>
    }

    return (
        <>
        <ul className="live-container" role="list">
            {renderItems()}
        </ul>
        {renderCloseBtn()}
        </>
    );
};

export default LiveContainer;